'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { GeoforgeLogoWithText } from '@/components/icons/geoforge-logo-with-text';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { forgotPassword } from '@/app/actions/auth';
import { CAPTCHA_FAILED_MESSAGE } from '@/utils/auth/captcha';
import { CaptchaWidget, useCaptcha } from './captcha';
import { SignupErrorMessage } from './signup-error-message';

const ForgotPassword = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const router = useRouter();
  const { widgetRef, attachCaptchaToken, resetCaptcha } = useCaptcha();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(event.currentTarget);

    if (!(await attachCaptchaToken(formData))) {
      setError(CAPTCHA_FAILED_MESSAGE);
      setLoading(false);
      return;
    }

    const result = await forgotPassword(formData);

    if (result.isSuccess && result.data?.email) {
      router.push(`/verify-recovery?email=${encodeURIComponent(result.data.email)}`);
      return;
    }

    resetCaptcha();
    setError(result.error?.message || 'An error occurred');
    setLoading(false);
  };

  return (
    <div className="mx-auto w-full max-w-md rounded-md border border-gray-25 p-[25px]">
      <div className="mb-6 flex justify-center">
        <GeoforgeLogoWithText />
      </div>

      <div className="mb-6 text-center">
        <h1 className="mb-4 text-xl font-bold text-black">Forgot Password</h1>
        <p className="text-sm text-blue-25">Please enter your email to reset the password</p>
      </div>

      <form method="post" onSubmit={handleSubmit} className="flex flex-col gap-2">
        <div>
          <Label htmlFor="email">Email address</Label>
          <Input
            className="mt-1.5"
            type="email"
            placeholder="user@example.com"
            id="email"
            name="email"
            required
          />
        </div>

        {error && <SignupErrorMessage message={error} />}

        <CaptchaWidget ref={widgetRef} />

        <Button
          type="submit"
          isLoading={loading}
          loadingText="Sending..."
          className="mt-2 w-full bg-black"
        >
          Reset password
        </Button>
      </form>

      <div className="mt-6 text-center text-sm text-defaultFont">
        <Link href="/login" className="font-bold text-defaultFont hover:text-defaultFont/80">
          Back to Sign In
        </Link>
      </div>
    </div>
  );
};

export default ForgotPassword;
